import { apiObjectUrl, apiRequest, apiUpload } from '../../shared/api/client';

/**
 * Item master CRUD — mirrors Invetrak.Api.Endpoints.ItemEndpoints (camelCase).
 *   GET   /api/v1/items[?includeInactive]  -> ItemDto[]
 *   GET   /api/v1/items/{id}               -> ItemDto
 *   POST  /api/v1/items                    -> 201 ItemDto | 409 duplicate number
 *   PATCH /api/v1/items/{id}               -> ItemDto (item number is immutable)
 *   GET   /api/v1/items/{id}/uoms          -> ItemUomDto[] (units this item transacts in)
 * Reads need items:read, writes items:write (enforced server-side).
 *
 * Column limits worth respecting client-side, from tbl_ItemMaster:
 *   ItemNumber   varchar(100), unique
 *   Description  nvarchar(500)
 *   StandardCost decimal(19,4)
 *   CurrencyCode char(3), defaults 'USD'
 *
 * UOM conversions: an item has a stocking unit plus up to two more, CHAINED — the second
 * is defined relative to the FIRST, not to the stocking unit. Stocking CASE with
 * 1 CASE = 1000 EA and then 1 EA = 10 LB means 10000 LB per CASE. The server fans those
 * out through item-independent physical conversions and precomputes a factor per unit;
 * `listItemUoms` returns the result. Never convert quantities client-side — the server
 * owns that, so every caller (PWA, handheld, integrations) agrees to the digit.
 */

export const ITEM_NUMBER_MAX = 100;
export const ITEM_DESCRIPTION_MAX = 500;

/** decimal(20,10) server-side; factors go below 1 whenever the other unit is larger. */
export const CONVERSION_FACTOR_DECIMALS = 10;

/**
 * Where a generated factor came from. The item defines at most two conversions; the server fans
 * those out through the tenant-wide physical conversions, so the generated set is usually larger
 * — and this is what answers "why is this unit in the list at all?".
 *
 * The three item-level sources beat the three global ones for the same unit, which is how an
 * item's own chain stays authoritative over a generic conversion.
 */
export type UomFactorSource =
  | 'stocking'
  | 'conversion1'
  | 'conversion2'
  | 'global_from_stocking'
  | 'global_from_conversion1'
  | 'global_from_conversion2';

export interface ItemUomDto {
  uomId: string;
  uomCode: string;
  displayName: string;
  uomType: string;
  /** How many of this unit equal ONE stocking unit — divide an entered quantity by it. */
  factor: number;
  isStockingUom: boolean;
  /** Suppressed from pick lists. Only ever true when the list was asked to include exclusions. */
  isExcluded: boolean;
  /** Null only for rows generated before the column existed; the next regeneration fills it in. */
  factorSource: UomFactorSource | null;
}


export interface ItemDto {
  itemId: string;
  itemNumber: string;
  description: string;
  itemTypeId: string | null;
  primaryUomId: string;
  isSerialized: boolean;
  isLotControlled: boolean;
  tracksExpiration: boolean;
  defaultShelfLifeDays: number | null;
  standardCost: number | null;
  currencyCode: string;
  isActive: boolean;
  createdAtUtc: string;
  lastModifiedUtc: string;
  conversionUomId: string | null;
  conversionFactor: number | null;
  conversionUom2Id: string | null;
  conversionFactor2: number | null;
  /**
   * Physical stock across every warehouse, and how many warehouses hold some. Read-only, appended
   * by tenant script 048.
   *
   * The seven physical buckets only — an open purchase order is not inventory, so an item with
   * nothing but one on the way can still be deactivated. Non-zero means the database will refuse to
   * deactivate this item or change its tracking switches, so the form disables them rather than
   * letting somebody set one and meet a 409 on save.
   *
   * Optional because an API in front of a database without 048 omits them.
   */
  stockedQty?: number;
  stockedWarehouses?: number;
  /**
   * Stock on its way: an open purchase order. Blocks DEACTIVATION only — an order against an
   * inactive item cannot be received, because the item is gone from the pickers the receipt uses.
   *
   * Deliberately does not lock the tracking switches: nothing has been received, so no existing
   * balance would be reinterpreted, and turning lot control on mid-order is useful because the lot
   * then gets captured at receipt.
   */
  onOrderQty?: number;
  onOrderWarehouses?: number;
  /**
   * The seven fields legacy's item master has always carried, added by tenant script 054.
   *
   * `upc` and `model` are legacy's UPC and ItemModel. `salesDescription` is DescriptionSales —
   * what a customer sees, as against `description`, which is what the warehouse sees.
   * `description2` and `description3` are free text legacy defines no meaning for.
   * `unitPrice` is the price volume-pricing discounts are taken off; nothing consumes it yet.
   * `requiresInspection` is legacy's InspectionRequired and nothing acts on it yet either.
   *
   * Optional on the read type because an API in front of a database without 054 omits them,
   * the same way 048's stock counts are optional above.
   */
  upc?: string | null;
  model?: string | null;
  salesDescription?: string | null;
  description2?: string | null;
  description3?: string | null;
  unitPrice?: number | null;
  requiresInspection?: boolean;
  /**
   * The photo elected to represent this item, or null when it has none. Read-only, added by
   * tenant script 057 so the list can show a thumbnail without a request per row just to learn
   * this id. Absent — not null — from an API in front of a database without 057.
   */
  defaultImageId?: string | null;
}

export interface CreateItemRequest {
  itemNumber: string;
  description: string;
  primaryUomId: string;
  itemTypeId?: string | null;
  isSerialized?: boolean | null;
  isLotControlled?: boolean | null;
  tracksExpiration?: boolean | null;
  defaultShelfLifeDays?: number | null;
  standardCost?: number | null;
  currencyCode?: string | null;
  isActive?: boolean | null;
  conversionUomId?: string | null;
  conversionFactor?: number | null;
  conversionUom2Id?: string | null;
  conversionFactor2?: number | null;
  /**
   * Warehouses to stock the item in. **Omitting this assigns every active warehouse** — it does
   * not mean "none". An item assigned nowhere never appears in Inventory Planning and is
   * skipped by ABC classification, while inventory posting does not require an assignment, so
   * an omission would create items that quietly never get counted. Send `[]` to mean none.
   */
  warehouseIds?: string[] | null;
  upc?: string | null;
  model?: string | null;
  salesDescription?: string | null;
  description2?: string | null;
  description3?: string | null;
  unitPrice?: number | null;
  requiresInspection?: boolean | null;
}

export interface UpdateItemRequest {
  description: string;
  primaryUomId: string;
  itemTypeId: string | null;
  isSerialized: boolean;
  isLotControlled: boolean;
  tracksExpiration: boolean;
  defaultShelfLifeDays: number | null;
  standardCost: number | null;
  currencyCode: string;
  isActive: boolean;
  conversionUomId: string | null;
  conversionFactor: number | null;
  conversionUom2Id: string | null;
  conversionFactor2: number | null;
  /**
   * Omitting this leaves the current assignments alone, so an edit that does not concern
   * warehouses cannot detach the item. Supplied, it REPLACES the set: warehouses that stay keep
   * their planning and cycle-count values, and removing one that still holds stock is refused
   * with a 409 naming it.
   */
  warehouseIds?: string[] | null;
  upc?: string | null;
  model?: string | null;
  salesDescription?: string | null;
  description2?: string | null;
  description3?: string | null;
  unitPrice?: number | null;
  requiresInspection?: boolean | null;
}

/** One warehouse that carries an item — a row of the item/warehouse assignment. */
export interface ItemWarehouseDto {
  warehouseId: string;
  warehouseCode: string;
  displayName: string;
  /** The WAREHOUSE's active flag. An assignment to a deactivated warehouse still exists. */
  isActive: boolean;
  /** Cleared on the Inventory Planning screen to mean "set up here but not carried". */
  isStocked: boolean;
  assignedAtUtc: string;
}

const COOKIE_INIT: RequestInit = { credentials: 'include' };

export async function listItems(
  includeInactive = false,
  signal?: AbortSignal,
): Promise<ItemDto[]> {
  const query = includeInactive ? '?includeInactive=true' : '';
  const { data } = await apiRequest<ItemDto[]>(`/v1/items/${query}`, {
    method: 'GET',
    signal,
    ...COOKIE_INIT,
  });
  return data;
}

export async function createItem(
  body: CreateItemRequest,
  signal?: AbortSignal,
): Promise<ItemDto> {
  const { data } = await apiRequest<ItemDto>('/v1/items/', {
    method: 'POST',
    body,
    signal,
    ...COOKIE_INIT,
  });
  return data;
}

export async function updateItem(
  id: string,
  body: UpdateItemRequest,
  signal?: AbortSignal,
): Promise<ItemDto> {
  const { data } = await apiRequest<ItemDto>(`/v1/items/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body,
    signal,
    ...COOKIE_INIT,
  });
  return data;
}

/**
 * The units this item can be transacted in, each with its factor to the stocking unit.
 * Server-generated: the stocking unit itself, the item's own chained conversions, and
 * everything reachable from any of those through the global physical conversions.
 * Ordered with the stocking unit first.
 */
export async function listItemUoms(
  itemId: string,
  signal?: AbortSignal,
): Promise<ItemUomDto[]> {
  const { data } = await apiRequest<ItemUomDto[]>(
    `/v1/items/${encodeURIComponent(itemId)}/uoms`,
    { method: 'GET', signal, ...COOKIE_INIT },
  );
  return data;
}

/**
 * The same list including suppressed units — the item screen's maintenance view.
 *
 * Separate from `listItemUoms` on purpose rather than a flag on it. Anything offering units to
 * transact in must use the plain call: the server's posting path reads the unfiltered-by-default
 * list, so a picker that quietly included exclusions would let an operator transact in a unit the
 * tenant deliberately hid. Two named functions make choosing the wrong one a visible mistake.
 */
export async function listItemUomsForMaintenance(
  itemId: string,
  signal?: AbortSignal,
): Promise<ItemUomDto[]> {
  const { data } = await apiRequest<ItemUomDto[]>(
    `/v1/items/${encodeURIComponent(itemId)}/uoms?includeExcluded=true`,
    { method: 'GET', signal, ...COOKIE_INIT },
  );
  return data;
}

/**
 * Suppresses one generated unit from pick lists, or restores it. Returns the item's full
 * maintenance view, so the caller redraws from the response rather than refetching.
 *
 * There is no equivalent for the FACTOR: it is derived from the item's chain and the tenant-wide
 * conversions and recomputed on every item save, so a typed-in value would be discarded later by
 * an unrelated edit. Change the chain or the global conversion instead.
 */
export async function setItemUomExcluded(
  itemId: string,
  uomId: string,
  isExcluded: boolean,
  signal?: AbortSignal,
): Promise<ItemUomDto[]> {
  const { data } = await apiRequest<ItemUomDto[]>(
    `/v1/items/${encodeURIComponent(itemId)}/uoms/${encodeURIComponent(uomId)}/excluded`,
    { method: 'PUT', body: { isExcluded }, signal, ...COOKIE_INIT },
  );
  return data;
}

/**
 * The warehouses this item is assigned to.
 *
 * Its own call rather than a field on the item: the list endpoint would otherwise carry a row
 * per (item, warehouse), and the only screen that needs it is the item editor.
 */
export async function listItemWarehouses(
  itemId: string,
  signal?: AbortSignal,
): Promise<ItemWarehouseDto[]> {
  const { data } = await apiRequest<ItemWarehouseDto[]>(
    `/v1/items/${encodeURIComponent(itemId)}/warehouses`,
    { method: 'GET', signal, ...COOKIE_INIT },
  );
  return data;
}

/**
 * Converts an entered quantity to the stocking unit for DISPLAY ONLY.
 *
 * The server does the real conversion when posting; this exists so an operator can see
 * "2,000 EA = 2 CASE" while typing. Never send the result anywhere — that would put a
 * second, divergent implementation of the arithmetic on the wire.
 */
export function previewStockingQuantity(
  enteredQuantity: number,
  factor: number,
): number | null {
  if (!Number.isFinite(enteredQuantity) || !Number.isFinite(factor) || factor <= 0) return null;
  // Matches the server's ROUND(..., 5).
  return Math.round((enteredQuantity / factor) * 1e5) / 1e5;
}

// ---------------------------------------------------------------------------
// Photos — tenant script 055
// ---------------------------------------------------------------------------

/**
 * One photo's metadata. The BYTES are not here: a list carrying its blobs would be tens of
 * megabytes on an item with a few pictures. Fetch them one at a time with
 * {@link itemImageObjectUrl}.
 */
export interface ItemImageDto {
  itemImageId: string;
  itemId: string;
  contentType: string;
  /** The one shown on the item and inquiry screens. An item with any photos has exactly one. */
  isDefault: boolean;
  displayOrder: number;
  fileName: string | null;
  sizeBytes: number | null;
  uploadedAtUtc: string;
}

/** The server's cap. Checked here too so a 5 MB photo is refused before it is uploaded. */
export const ITEM_IMAGE_MAX_BYTES = 5 * 1024 * 1024;

export async function listItemImages(
  itemId: string,
  signal?: AbortSignal,
): Promise<ItemImageDto[]> {
  const { data } = await apiRequest<ItemImageDto[]>(
    `/v1/items/${encodeURIComponent(itemId)}/images`,
    { method: 'GET', signal, ...COOKIE_INIT },
  );
  return data;
}

/** Uploads one photo and returns the item's full list. Every write here does the same. */
export async function uploadItemImage(itemId: string, file: File): Promise<ItemImageDto[]> {
  const { data } = await apiUpload<ItemImageDto[]>(
    `/v1/items/${encodeURIComponent(itemId)}/images`,
    file,
  );
  return data;
}

export async function makeItemImageDefault(
  itemId: string,
  itemImageId: string,
): Promise<ItemImageDto[]> {
  const { data } = await apiRequest<ItemImageDto[]>(
    `/v1/items/${encodeURIComponent(itemId)}/images/${encodeURIComponent(itemImageId)}/default`,
    { method: 'PUT', ...COOKIE_INIT },
  );
  return data;
}

export async function removeItemImage(
  itemId: string,
  itemImageId: string,
): Promise<ItemImageDto[]> {
  const { data } = await apiRequest<ItemImageDto[]>(
    `/v1/items/${encodeURIComponent(itemId)}/images/${encodeURIComponent(itemImageId)}`,
    { method: 'DELETE', ...COOKIE_INIT },
  );
  return data;
}

/**
 * An object URL for one photo's bytes.
 *
 * Not an <img src> pointing straight at the API: an img tag cannot send X-Warehouse-Id, and every
 * warehouse-scoped endpoint refuses a request without it. Exempting the image endpoint from
 * scoping would have made a bare img work, and would also have let one 3PL client's staff read a
 * photo of another client's stock.
 *
 * The CALLER owns the returned URL and must revoke it — see apiObjectUrl.
 */
export function itemImageObjectUrl(
  itemId: string,
  itemImageId: string,
  signal?: AbortSignal,
): Promise<string> {
  return apiObjectUrl(
    `/v1/items/${encodeURIComponent(itemId)}/images/${encodeURIComponent(itemImageId)}/content`,
    signal,
  );
}

// ---------------------------------------------------------------------------
// Documents — tenant script 056
// ---------------------------------------------------------------------------

/**
 * One file attached to an item. The BYTES are not here; fetch them with
 * {@link downloadItemDocument}.
 */
export interface ItemDocumentDto {
  itemDocumentId: string;
  itemId: string;
  /** The file's name. Editable afterwards — a scanner calling it "scan0042.pdf" is common. */
  documentName: string;
  /** The operator's own words, legacy's DocumentDescription. Null when nothing was typed. */
  description: string | null;
  contentType: string;
  sizeBytes: number | null;
  uploadedAtUtc: string;
}

/** The server's cap — twice the photo limit, because a scanned SDS is bigger than a photo. */
export const ITEM_DOCUMENT_MAX_BYTES = 10 * 1024 * 1024;

export async function listItemDocuments(
  itemId: string,
  signal?: AbortSignal,
): Promise<ItemDocumentDto[]> {
  const { data } = await apiRequest<ItemDocumentDto[]>(
    `/v1/items/${encodeURIComponent(itemId)}/documents`,
    { method: 'GET', signal, ...COOKIE_INIT },
  );
  return data;
}

export async function uploadItemDocument(
  itemId: string,
  file: File,
  description?: string,
): Promise<ItemDocumentDto[]> {
  const { data } = await apiUpload<ItemDocumentDto[]>(
    `/v1/items/${encodeURIComponent(itemId)}/documents`,
    file,
    'file',
    description ? { description } : undefined,
  );
  return data;
}

export async function renameItemDocument(
  itemId: string,
  itemDocumentId: string,
  documentName: string,
  description: string | null,
): Promise<ItemDocumentDto[]> {
  const { data } = await apiRequest<ItemDocumentDto[]>(
    `/v1/items/${encodeURIComponent(itemId)}/documents/${encodeURIComponent(itemDocumentId)}`,
    { method: 'PATCH', body: { documentName, description }, ...COOKIE_INIT },
  );
  return data;
}

export async function removeItemDocument(
  itemId: string,
  itemDocumentId: string,
): Promise<ItemDocumentDto[]> {
  const { data } = await apiRequest<ItemDocumentDto[]>(
    `/v1/items/${encodeURIComponent(itemId)}/documents/${encodeURIComponent(itemDocumentId)}`,
    { method: 'DELETE', ...COOKIE_INIT },
  );
  return data;
}

/**
 * Saves one document to the operator's machine.
 *
 * The same reason photos are fetched rather than linked: a plain <a href> cannot send
 * X-Warehouse-Id, and the endpoint refuses a request without it. So the bytes are fetched with
 * the header, wrapped in an object URL, and handed to a synthetic anchor.
 *
 * The URL is revoked immediately after the click. The browser has already begun the download by
 * then — it does not read the blob lazily — so nothing is lost, and holding it would pin the
 * whole file in memory for the life of the page.
 */
export async function downloadItemDocument(
  itemId: string,
  document_: ItemDocumentDto,
): Promise<void> {
  const url = await apiObjectUrl(
    `/v1/items/${encodeURIComponent(itemId)}/documents/${encodeURIComponent(document_.itemDocumentId)}/content`,
  );
  try {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = document_.documentName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * What an item's history permits — tenant script 077.
 *
 * legacy will not let you change a stocking unit once the item has been moved, purchased or sold,
 * and neither will we: every balance, ledger row and price tier is expressed in the current unit
 * and none of them would be converted.
 *
 * `hasBalance` is reported but is NOT part of `canChangeStockingUom` — legacy uses it for a
 * different lock, the active flag.
 */
export interface ItemPropertiesDto {
  itemId: string;
  hasTransactions: boolean;
  hasPurchaseOrders: boolean;
  hasSalesOrders: boolean;
  hasBalance: boolean;
  canChangeStockingUom: boolean;
}

export async function getItemProperties(
  itemId: string,
  signal?: AbortSignal,
): Promise<ItemPropertiesDto> {
  const { data } = await apiRequest<ItemPropertiesDto>(
    `/v1/items/${encodeURIComponent(itemId)}/properties`,
    { method: 'GET', signal, credentials: 'include' },
  );
  return data;
}
